import { useState } from 'react';
import { StatePanel, V2Badge, V2Button, V2Card, V2EmptyState } from '@sakhtar/design-system/react';
import { apiPut, ApiError } from '../../lib/apiClient.js';
import type {
  CustomerCommunicationChannel,
  CustomerCommunicationConsent,
  CustomerCommunicationConsentState,
  CustomerCommunicationEvent,
  CustomerCommunicationItem,
  CustomerCommunicationTimelineResponse,
} from '../../lib/types/customers.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { exactWhen, relativeWhen } from '../../lib/date/relativeWhen.js';
import { CustomerRelationshipAnalysisCard } from './CustomerRelationshipAnalysisCard.js';
import './customerCommunications.css';

const CHANNEL_LABEL: Readonly<Record<CustomerCommunicationChannel, string>> = {
  sms: 'پیامک',
  voice: 'تماس صوتی',
};

const CONSENT_LABEL: Readonly<Record<CustomerCommunicationConsentState, string>> = {
  unknown: 'ثبت نشده',
  opted_in: 'مجاز',
  opted_out: 'عدم رضایت',
};

const EVENT_LABEL: Readonly<Record<CustomerCommunicationEvent, string>> = {
  queued: 'در صف ارسال',
  provider_accepted: 'پذیرفته‌شده توسط سرویس‌دهنده',
  delivered: 'تحویل‌شده',
  read: 'خوانده‌شده',
  failed: 'ناموفق',
  cancelled: 'لغوشده',
  received: 'دریافت‌شده',
  started: 'شروع تماس',
  answered: 'پاسخ داده‌شده',
  completed: 'پایان‌یافته',
  missed: 'بی‌پاسخ',
  analysis_completed: 'تحلیل ساختار یار تکمیل شد',
};

const PURPOSE_LABEL = {
  transactional: 'تراکنشی',
  service: 'خدماتی',
  marketing: 'بازاریابی',
} as const;

const DIRECTION_LABEL = {
  inbound: 'ورودی',
  outbound: 'خروجی',
} as const;

function eventTone(event: CustomerCommunicationEvent) {
  if (event === 'failed' || event === 'missed' || event === 'cancelled') return 'danger' as const;
  if (event === 'delivered' || event === 'completed' || event === 'analysis_completed') {
    return 'success' as const;
  }
  if (event === 'queued' || event === 'provider_accepted' || event === 'started') {
    return 'info' as const;
  }
  return 'neutral' as const;
}

function consentTone(state: CustomerCommunicationConsentState) {
  if (state === 'opted_in') return 'success' as const;
  if (state === 'opted_out') return 'danger' as const;
  return 'neutral' as const;
}

function ConsentCard(props: {
  readonly channel: CustomerCommunicationChannel;
  readonly consent: CustomerCommunicationConsent | null;
  readonly loading: boolean;
  readonly saving: boolean;
  readonly onChange: (state: CustomerCommunicationConsentState) => void;
}) {
  const state = props.consent?.state ?? 'unknown';
  return (
    <V2Card>
      <section className="c360-comms-consent" aria-label={'رضایت ' + CHANNEL_LABEL[props.channel]}>
        <div className="c360-comms-consent__head">
          <div>
            <h3>{CHANNEL_LABEL[props.channel]}</h3>
            <p>
              {props.channel === 'sms'
                ? 'پیام‌های خدماتی و بازاریابی'
                : 'تماس، ضبط مکالمه و تحلیل تماس'}
            </p>
          </div>
          <V2Badge tone={consentTone(state)}>
            {props.loading ? 'در حال بررسی…' : CONSENT_LABEL[state]}
          </V2Badge>
        </div>
        <div className="c360-comms-consent__actions" aria-label="تغییر وضعیت رضایت">
          {(['opted_in', 'opted_out', 'unknown'] as const).map((value) => (
            <V2Button
              key={value}
              variant={state === value ? 'primary' : 'ghost'}
              disabled={props.loading || props.saving}
              onClick={() => props.onChange(value)}
            >
              {CONSENT_LABEL[value]}
            </V2Button>
          ))}
        </div>
        {props.consent?.recordedAt ? (
          <small title={exactWhen(props.consent.recordedAt)}>
            آخرین ثبت: {relativeWhen(props.consent.recordedAt)}
            {props.consent.source ? ' · ' + props.consent.source : ''}
          </small>
        ) : null}
      </section>
    </V2Card>
  );
}

function TimelineRow({ item }: { readonly item: CustomerCommunicationItem }) {
  return (
    <li className="c360-comms-row">
      <div className="c360-comms-row__mark" data-channel={item.channel}>
        {item.channel === 'sms' ? 'SMS' : 'VOIP'}
      </div>
      <div className="c360-comms-row__body">
        <div className="c360-comms-row__title">
          <strong>
            {CHANNEL_LABEL[item.channel]} {DIRECTION_LABEL[item.direction]}
          </strong>
          <V2Badge tone={eventTone(item.latestEvent)}>{EVENT_LABEL[item.latestEvent]}</V2Badge>
        </div>
        <div className="c360-comms-row__meta">
          <span className="c360-ltr" dir="ltr">
            {item.endpoint}
          </span>
          <span>{PURPOSE_LABEL[item.purpose]}</span>
          {item.provider ? <span>{item.provider}</span> : null}
          {item.templateKey ? <span>قالب: {item.templateKey}</span> : null}
        </div>
        {item.reasonCode ? <p className="c360-comms-row__error">علت: {item.reasonCode}</p> : null}
      </div>
      <time dateTime={item.latestEventAt} title={exactWhen(item.latestEventAt)}>
        {relativeWhen(item.latestEventAt)}
      </time>
    </li>
  );
}

export function CustomerCommunicationsPanel(props: {
  readonly base: string;
  readonly partyRef: string;
  readonly channel?: CustomerCommunicationChannel;
  /** Show the AI customer analysis under the history. Off on Customer 360 (it is on the overview). */
  readonly showAnalysis?: boolean;
}) {
  const encodedRef = encodeURIComponent(props.partyRef);
  const timeline = useApiGet<CustomerCommunicationTimelineResponse>(
    props.base + '/api/v1/crm/communications?partyRef=' + encodedRef + '&limit=100',
    [props.partyRef],
  );
  const smsConsent = useApiGet<CustomerCommunicationConsent>(
    props.base + '/api/v1/crm/communications/consents/' + encodedRef + '?channel=sms',
    [props.partyRef],
  );
  const voiceConsent = useApiGet<CustomerCommunicationConsent>(
    props.base + '/api/v1/crm/communications/consents/' + encodedRef + '?channel=voice',
    [props.partyRef],
  );

  const [saving, setSaving] = useState<CustomerCommunicationChannel | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function updateConsent(
    channel: CustomerCommunicationChannel,
    state: CustomerCommunicationConsentState,
  ) {
    setSaving(channel);
    setSaveError(null);
    try {
      await apiPut<CustomerCommunicationConsent>(
        props.base + '/api/v1/crm/communications/consents/' + encodedRef,
        { channel, state, source: 'customer_360_manual' },
      );
      if (channel === 'sms') smsConsent.reload();
      else voiceConsent.reload();
    } catch (error) {
      setSaveError(
        error instanceof ApiError && error.status === 403
          ? 'برای تغییر رضایت ارتباطی دسترسی ندارید.'
          : 'ثبت وضعیت رضایت انجام نشد. دوباره تلاش کنید.',
      );
    } finally {
      setSaving(null);
    }
  }

  if (timeline.status === 'error') {
    return (
      <StatePanel
        variant={timeline.error.status === 403 ? 'permission_denied' : 'network_error'}
        title={
          timeline.error.status === 403
            ? 'برای مشاهده ارتباطات این مشتری دسترسی ندارید'
            : 'تاریخچه ارتباطات در دسترس نیست'
        }
      />
    );
  }

  const communications =
    timeline.status === 'ready'
      ? timeline.data.communications.filter(
          (item) => props.channel === undefined || item.channel === props.channel,
        )
      : [];
  const sms = smsConsent.status === 'ready' ? smsConsent.data : null;
  const voice = voiceConsent.status === 'ready' ? voiceConsent.data : null;
  const channels: readonly CustomerCommunicationChannel[] =
    props.channel === undefined ? ['sms', 'voice'] : [props.channel];

  return (
    <section
      className="c360-comms"
      aria-label="ارتباطات مشتری"
      data-scoped={props.channel === undefined ? undefined : 'true'}
    >
      <div className="c360-comms-head">
        <div>
          <h2>
            {props.channel === 'sms'
              ? 'پیامک‌های مشتری'
              : props.channel === 'voice'
                ? 'تماس‌های مشتری'
                : 'ارتباطات مشتری'}
          </h2>
          <p>
            {props.channel === 'sms'
              ? 'وضعیت پیامک‌ها و رضایت ارتباط پیامکی این مشتری.'
              : props.channel === 'voice'
                ? 'وضعیت تماس‌ها و رضایت ارتباط صوتی این مشتری.'
                : 'پیامک‌ها، تماس‌ها و رضایت ارتباطی؛ فقط از داده‌های ثبت‌شده و مجاز.'}
          </p>
        </div>
        <V2Badge tone="info">
          {timeline.status === 'loading'
            ? 'در حال دریافت…'
            : communications.length.toLocaleString('fa-IR') + ' رویداد'}
        </V2Badge>
      </div>

      <div className="c360-comms-consents" data-count={channels.length}>
        {channels.map((channel) => (
          <ConsentCard
            key={channel}
            channel={channel}
            consent={channel === 'sms' ? sms : voice}
            loading={
              channel === 'sms'
                ? smsConsent.status === 'loading'
                : voiceConsent.status === 'loading'
            }
            saving={saving === channel}
            onChange={(state) => void updateConsent(channel, state)}
          />
        ))}
      </div>

      {saveError ? <p className="c360-comms-alert">{saveError}</p> : null}

      {/* Customer-level AI analysis lives on the Customer 360 overview; the channel workspaces
          (SMS panel / call workspace) keep it below the history via `showAnalysis`. */}
      {props.showAnalysis ? (
        <div className="c360-comms__analysis">
          <CustomerRelationshipAnalysisCard base={props.base} partyRef={props.partyRef} />
        </div>
      ) : null}

      <V2Card clipped>
        <div className="c360-comms-timeline-head">
          <div>
            <h3>تاریخچه ارتباطات</h3>
            <p>متن پیام و فایل مکالمه برای حفاظت از حریم خصوصی در این فهرست ذخیره نمی‌شود.</p>
          </div>
          <V2Button
            variant="ghost"
            disabled={timeline.status === 'loading'}
            onClick={timeline.reload}
          >
            بروزرسانی
          </V2Button>
        </div>
        {timeline.status === 'loading' ? (
          <div className="c360-comms-loading">در حال دریافت تاریخچه…</div>
        ) : communications.length === 0 ? (
          <V2EmptyState
            icon="phone"
            title="هنوز ارتباطی ثبت نشده است"
            description={
              props.channel === 'sms'
                ? 'هنوز پیامکی برای این مشتری ثبت نشده است.'
                : props.channel === 'voice'
                  ? 'هنوز تماسی برای این مشتری ثبت نشده است.'
                  : 'پس از اتصال ارسال پیامک یا مرکز تماس، وضعیت‌ها در همین بخش نمایش داده می‌شوند.'
            }
            compact
          />
        ) : (
          <ol className="c360-comms-list">
            {communications.map((item) => (
              <TimelineRow key={item.ref} item={item} />
            ))}
          </ol>
        )}
      </V2Card>
    </section>
  );
}
